import { CompanyProfile, SubscriptionManagementPermissions } from '../../../administration/models/company-profile.model';
import { ProductTypes } from 'common/src/modules/products/product-types';
import { GradientColor } from 'common/src/models';
import { AppNameEnum } from 'common/src/modules/start-screen-page/launchpad.model';
import { UserPermissionsService } from 'common/src/services/user-permissions.service';
import { UserShortcutEnum } from '../../../hrm/enums';

export interface SideNavigationModulesChildItem {
  title: string;
  icon: string;
  routerLink: string;
  disabled?: boolean;
  show?: boolean;
  action?: string;
  appName?: AppNameEnum;
  shortcutKey?: UserShortcutEnum;
}

export interface SideNavigationModulesItem {
  title: string;
  icon: string;
  show?: boolean;
  color: string;
  childrenGroups: {
    title: string;
    children: SideNavigationModulesChildItem[];
  }[];
}

export function getNavItems(
  userPermissionsService: UserPermissionsService,
  reportingTypeSimplified: boolean,
  companyProfile: CompanyProfile,
): SideNavigationModulesItem[] {
  const canViewAccounting: boolean = userPermissionsService.canViewAccounting();
  const canViewAdministration: boolean = userPermissionsService.canViewAdministration();
  const canViewEcommerce: boolean = userPermissionsService.canViewEcommerce();
  const canViewPartners: boolean = userPermissionsService.canViewPartners();
  const canViewProducts: boolean = userPermissionsService.canViewProducts();
  const canViewServices: boolean = userPermissionsService.canViewServices();
  const canViewTeam: boolean = userPermissionsService.canViewTeam();
  const canViewWarehouse: boolean = userPermissionsService.canViewWarehouse();
  const canViewWholesale: boolean = userPermissionsService.canViewWholesale();

  const canManageWarehouse: boolean = userPermissionsService.canManageWarehouse();
  const canManageProducts: boolean = userPermissionsService.canManageProducts();
  const canManagePartners: boolean = userPermissionsService.canManagePartners();
  const canManageAccounting: boolean = userPermissionsService.canManageAccounting();
  const canManageEcommerce: boolean = userPermissionsService.canManageEcommerce();
  const canManageWholesale: boolean = userPermissionsService.canManageWholesale();
  const canManageServices: boolean = userPermissionsService.canManageServices();

  const wholesaleEnabled: boolean = companyProfile.subscriptionManagement.channelOptions.wholesalesEnabled;
  const ecommerceEnabled: boolean = companyProfile.subscriptionManagement.channelOptions.ecommerceEnabled;
  const servicesEnabled: boolean = companyProfile.subscriptionManagement.channelOptions.servicesEnabled;

  const isFreePlan: boolean = companyProfile.isFreePlan;
  const isAccountingPlan: boolean = companyProfile.isAccountingPlan;
  const isTeamPlan: boolean = companyProfile.isTeamPlan;
  const isBasicPlan: boolean = companyProfile.isBasicPlan;
  const isStandardPlan: boolean = companyProfile.isStandardPlan;
  const isProPlan: boolean = companyProfile.isProPlan;
  // const isMasterWorkspace: boolean = companyProfile.subscriptionManagement.master;
  const sbcPermissions: SubscriptionManagementPermissions = companyProfile.subscriptionManagement.subscriptionPermissions;
  const goodsProductTypeEnabled: boolean = companyProfile.productOptions.goodsEnabled;
  const servicesProductTypeEnabled: boolean = companyProfile.productOptions.servicesEnabled;
  const digitalProductTypeEnabled: boolean = companyProfile.productOptions.digitalEnabled;
  const bundleProductTypeEnabled: boolean = companyProfile.productOptions.bundlesEnabled;
  const corporatePartnersEnabled: boolean = companyProfile.partnerOptions.corporateEnabled;
  const privatePartnersEnabled: boolean = companyProfile.partnerOptions.privateEnabled;

  const productTypeRedirect: ProductTypes = goodsProductTypeEnabled
    ? ProductTypes.GOODS
    : servicesProductTypeEnabled
      ? ProductTypes.SERVICES
      : ProductTypes.DIGITAL;

  const ACCOUNTING_ITEMS: SideNavigationModulesItem = {
    title: 'MODULE.ACCOUNTING',
    icon: 'calculator',
    show: canViewAccounting,
    color: GradientColor.VIOLET,
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'APP.OUTGOING_INVOICES',
            icon: 'outgoing-invoice',
            routerLink: '/accounting/outgoing-invoice',
            disabled: false,
            action: 'createNewInvoice',
            appName: AppNameEnum.OUTGOING_INVOICES,
            shortcutKey: UserShortcutEnum.OUTGOING_INVOICES,
          },
          {
            title: 'APP.INCOMING_INVOICES',
            icon: 'incoming-invoice',
            routerLink: '/accounting/incoming-invoice',
            disabled: false,
            action: 'createNewIncomingInvoice',
            appName: AppNameEnum.INCOMING_INVOICES,
            shortcutKey: UserShortcutEnum.INCOMING_INVOICES
          },
          {
            title: 'APP.PAYMENTS',
            icon: 'payment',
            routerLink: '/accounting/payments',
            disabled: false,
            appName: AppNameEnum.PAYMENTS,
            shortcutKey: UserShortcutEnum.PAYMENTS
          },
          {
            title: 'APP.CREDIT_NOTES',
            icon: 'credit-note',
            routerLink: '/accounting/credit-note',
            disabled: false,
            action: 'createNewCreditNote',
            appName: AppNameEnum.CREDIT_NOTES,
            shortcutKey: UserShortcutEnum.CREDIT_NOTES
          },
          {
            title: 'APP.RECURRING_INVOICES',
            icon: 'subscriptions',
            routerLink: '/accounting/recurring-invoice',
            disabled: false,
            action: 'createNewSubscription',
            show: (!isAccountingPlan && !isTeamPlan) || ((isAccountingPlan || isTeamPlan) && sbcPermissions.RECURRING_INVOICES),
            appName: AppNameEnum.SUBSCRIPTIONS,
            shortcutKey: UserShortcutEnum.SUBSCRIPTIONS
          },
        ]
      },
      {
        title: 'LAUNCH_PAD.ACCOUNTING_EXPORT',
        children: [
          {
            title: 'REPORTING_PERIODS.REPORTING_PERIODS',
            icon: 'accountant-report',
            routerLink: '/accounting/reporting-periods',
            disabled: false,
            appName: AppNameEnum.REPORTING_PERIODS,
            shortcutKey: UserShortcutEnum.REPORTING_PERIODS
          },
          {
            title: 'TAX_REPORTS.PRELIMINARY_RETURN',
            icon: 'tax-reports',
            routerLink: '/accounting/vat-reports',
            disabled: false,
            appName: AppNameEnum.VAT_REPORTS,
            shortcutKey: UserShortcutEnum.VAT_RETURN
          },
        ]
      },
      {
        title: 'MODULE.ANALYTICS',
        children: [
          {
            title: 'APP.OPEN_ITEM_LIST',
            icon: 'open-items-list',
            routerLink: '/accounting/open-items',
            disabled: false,
            appName: AppNameEnum.OPEN_ITEM_LIST,
            shortcutKey: UserShortcutEnum.OPEN_ITEM_LIST
          },
          {
            title: 'CASH_FLOW.CASH_FLOW',
            icon: 'cashflow',
            routerLink: '/accounting/cashflow',
            appName: AppNameEnum.CASHFLOW,
            shortcutKey: UserShortcutEnum.CASHFLOW
          },
          {
            title: 'APP.PAYMENTS_CALENDAR',
            icon: 'payments-calendar',
            routerLink: '/accounting/payments-calendar',
            disabled: false,
            appName: AppNameEnum.PAYMENT_CALENDAR,
            shortcutKey: UserShortcutEnum.PAYMENT_CALENDAR
          },
          {
            title: 'SCHEDULED_PAYMENT.SCHEDULED_PAYMENTS',
            icon: 'recurring-costs',
            routerLink: '/accounting/scheduled-payment',
            disabled: false,
            action: 'createNewScheduledPayment',
            appName: AppNameEnum.SCHEDULED_PAYMENTS,
            shortcutKey: UserShortcutEnum.SCHEDULED_PAYMENT
          },
          {
            title: 'FIXED_ASSETS.ASSETS_DIRECTORY',
            icon: 'fixed-assets',
            routerLink: '/accounting/fixed-assets',
            disabled: false,
            appName: AppNameEnum.FIXED_ASSETS,
            shortcutKey: UserShortcutEnum.FIXED_ASSETS
          },

          {
            title: 'GOODS_REGISTER.GOODS_REGISTER',
            icon: 'goods-register',
            show: reportingTypeSimplified,
            routerLink: '/accounting/goods-register',
            disabled: false,
            appName: AppNameEnum.GOODS_REGISTER,
            shortcutKey: UserShortcutEnum.GOODS_REGISTER
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'APP.BANK_ACCOUNTS',
            icon: 'account',
            routerLink: '/accounting/bank-accounts',
            disabled: false,
            action: 'createBankAccount',
            appName: AppNameEnum.BANK_ACCOUNT,
            shortcutKey: UserShortcutEnum.BANK_ACCOUNT
          },
          {
            title: 'COST_CENTER.COST_CENTERS',
            icon: 'costs_center',
            routerLink: '/accounting/cost-center',
            disabled: false,
            appName: AppNameEnum.COST_CENTER,
            shortcutKey: UserShortcutEnum.COST_CENTER
          },
          {
            title: 'GL_ACCOUNTS.LEDGER_ACCOUNTS',
            icon: 'gl-accounts',
            routerLink: '/accounting/gl-accounts',
            disabled: false,
            appName: AppNameEnum.GL_ACCOUNT,
            shortcutKey: UserShortcutEnum.GL_ACCOUNT
          },
          {
            title: 'APP.SETTINGS',
            show: canManageAccounting,
            icon: 'options-2',
            routerLink: '/accounting/settings'
          }
        ]
      }
    ],
  };

  const TRADE_ITEMS: SideNavigationModulesItem = {
    title: 'APP.SALES',
    icon: 'cash',
    show: isTeamPlan
      ? canViewWholesale
      : canViewWholesale
        && (wholesaleEnabled || isFreePlan || isAccountingPlan || isBasicPlan || companyProfile.isStandardPlan || companyProfile.isProPlan),
    // show: canViewWholesale || canViewServices,
    color: GradientColor.YELLOW,
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'APP.OFFERS',
            show: isTeamPlan
              ? canViewWholesale && sbcPermissions.OFFERS
              : canViewWholesale && (
                wholesaleEnabled
                || isFreePlan
                || isAccountingPlan
                || isBasicPlan
                || isStandardPlan
                || isProPlan
                || (servicesEnabled && !wholesaleEnabled && !ecommerceEnabled)
                || sbcPermissions.OFFERS
              ),
            icon: 'offer',
            routerLink: '/trade/offers',
            disabled: false,
            action: 'createNewOffer',
            appName: AppNameEnum.OFFERS,
            shortcutKey: UserShortcutEnum.OFFERS
          },
          {
            title: 'APP.SALES_ORDERS',
            show: isTeamPlan
              ? canViewWholesale && sbcPermissions.SALES_ORDERS
              : canViewWholesale &&
                (
                  wholesaleEnabled
                  || sbcPermissions.SALES_ORDERS
                  || isFreePlan
                  || isBasicPlan
                ),
            icon: 'sales-order',
            routerLink: '/trade/sales-order',
            disabled: false,
            action: 'createNewOrder',
            appName: AppNameEnum.SALES_ORDERS,
            shortcutKey: UserShortcutEnum.SALES_ORDERS
          },
          {
            title: companyProfile.isInventoryExtension
              ? 'APP.OUTGOING_DELIVERIES'
              : 'COMMON.DELIVERY_NOTES',
            icon: 'outgoing-delivery',
            routerLink: '/warehouse/outgoing-delivery',
            action: 'createNewDeliveryNote',
            appName: AppNameEnum.OUTGOING_DELIVERIES,
            shortcutKey: UserShortcutEnum.OUTGOING_DELIVERIES,
            show: isTeamPlan
              ? sbcPermissions.OUTGOING_DELIVERIES
              : true,
            // show: sbcPermissions.OUTGOING_DELIVERIES,
          },
          {
            title: 'PRICE.SALES_PRICES',
            show: isTeamPlan
              ? (canViewWholesale || canViewEcommerce || canViewServices) && sbcPermissions.SALES_PRICES
              : (canViewWholesale || canViewServices) && (wholesaleEnabled  || servicesEnabled || isFreePlan || isAccountingPlan)
                || (canViewWholesale || canViewEcommerce || canViewServices) && (wholesaleEnabled || ecommerceEnabled  || servicesEnabled)
                || sbcPermissions.SALES_PRICES,
            icon: 'pricetag',
            routerLink: `/trade/prices/sales/${productTypeRedirect}`,
            disabled: false,
          },
        ]
      },
      {
        title: 'MODULE.PURCHASES',
        children: [
          {
            title: 'APP.PURCHASE_ORDERS',
            show: isTeamPlan
              ? (canViewWholesale || canViewEcommerce) && sbcPermissions.PURCHASE_ORDERS
              : (canViewWholesale || canViewEcommerce) && ((wholesaleEnabled || ecommerceEnabled) && (isTeamPlan || isAccountingPlan))
                || (sbcPermissions.PURCHASE_ORDERS),
            // show: (canViewWholesale || canViewServices) && (wholesaleEnabled || servicesEnabled),
            icon: 'purchase-order',
            routerLink: '/trade/purchase-order',
            disabled: false,
            action: 'createNewPurchaseOrder',
            appName: AppNameEnum.PURCHASE_ORDERS,
            shortcutKey: UserShortcutEnum.PURCHASE_ORDERS
          },
          {
            title: 'APP.INCOMING_DELIVERIES',
            icon: 'incoming-delivery',
            routerLink: '/warehouse/incoming-delivery',
            appName: AppNameEnum.INCOMING_DELIVERIES,
            show: sbcPermissions.INCOMING_DELIVERIES,
            shortcutKey: UserShortcutEnum.INCOMING_DELIVERIES
          },
          {
            title: 'PRICE.PURCHASE_PRICES',
            show: isTeamPlan
              ? (canViewWholesale || canViewEcommerce || canViewServices) && sbcPermissions.PURCHASE_PRICES
              : (canViewWholesale || canViewServices) && (wholesaleEnabled  || servicesEnabled || isFreePlan || isAccountingPlan)
                || (canViewWholesale || canViewEcommerce || canViewServices) && (wholesaleEnabled || ecommerceEnabled  || servicesEnabled)
                || sbcPermissions.PURCHASE_PRICES,
            // show: (canViewWholesale || canViewServices) && (wholesaleEnabled || servicesEnabled),
            icon: 'pricetag',
            routerLink: `/trade/prices/purchase/${productTypeRedirect}`,
            disabled: false,
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'MODULE.CONTROL_CENTER',
            icon: 'compass',
            routerLink: '/control-center',
            show: isTeamPlan
              ? (canViewAccounting || canViewWarehouse || canViewWholesale || canViewAdministration) && sbcPermissions.CONTROL_CENTER
              : canViewAccounting || canViewWarehouse || canViewWholesale || canViewAdministration,
            appName: AppNameEnum.CONTROL_CENTER,
          },
          {
            title: 'APP.SETTINGS',
            show: isTeamPlan
              ? canManageWholesale
              : canManageWholesale && (
                wholesaleEnabled
                || isFreePlan
                || isAccountingPlan
                || isBasicPlan
                || isStandardPlan
                || isProPlan
                || (servicesEnabled && !wholesaleEnabled && !ecommerceEnabled)
              ),
            icon: 'options-2',
            routerLink: '/trade/settings'
          },
        ]
      },


      // {
      //   title: 'PRICE.SALES_PRICES',
      //   show: (canViewWholesale || canViewServices) && (wholesaleEnabled  || servicesEnabled || isFreePlan || isAccountingPlan),
      //   // show: (canViewWholesale || canViewServices) && (wholesaleEnabled || servicesEnabled),
      //   icon: 'pricetag',
      //   routerLink: `/trade/prices/sales/${productTypeRedirect}`,
      //   disabled: false
      // },
      // {
      //   title: 'PRICE.PURCHASE_PRICES',
      //   show: (canViewWholesale || canViewEcommerce || canViewServices) && (wholesaleEnabled || ecommerceEnabled  || servicesEnabled),
      //   icon: 'pricetag',
      //   routerLink: `/trade/prices/purchase/${productTypeRedirect}`,
      //   disabled: false
      // },
      // {
      //   title: 'APP.RETURNS',
      //   show: canViewWholesale && (wholesaleEnabled || ecommerceEnabled),
      //   // show: canViewWholesale && wholesaleEnabled,
      //   icon: 'exchange-return',
      //   routerLink: '/trade/exchanges-and-returns',
      //   disabled: false,
      //   locked: true,
      //   // action: 'createNewERA',
      //   appName: AppNameEnum.RETURNS
      // },
    ]
  };

  const WAREHOUSE_ITEMS: SideNavigationModulesItem = {
    title: 'LAUNCH_PAD.INVENTORY',
    icon: 'warehouse',
    show: canViewWarehouse && (
      wholesaleEnabled
      || ecommerceEnabled
      || isFreePlan
      || isBasicPlan
      || isStandardPlan
      || isProPlan
    ),
    color: GradientColor.ORANGE,
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'APP.STOCK_STATUS',
            show: goodsProductTypeEnabled && sbcPermissions.STOCK_STATUS,
            icon: 'cubes',
            routerLink: '/warehouse/stock-status/general-stock/1',
            appName: AppNameEnum.STOCK_STATUS,
            shortcutKey: UserShortcutEnum.STOCK_STATUS
          },
          {
            title: companyProfile.isInventoryExtension
              ? 'APP.OUTGOING_DELIVERIES'
              : 'COMMON.DELIVERY_NOTES',
            icon: 'outgoing-delivery',
            routerLink: '/warehouse/outgoing-delivery',
            action: 'createNewDeliveryNote',
            appName: AppNameEnum.OUTGOING_DELIVERIES,
            shortcutKey: UserShortcutEnum.OUTGOING_DELIVERIES
            // show: sbcPermissions.OUTGOING_DELIVERIES,
          },
          {
            title: 'APP.INCOMING_DELIVERIES',
            icon: 'incoming-delivery',
            routerLink: '/warehouse/incoming-delivery',
            appName: AppNameEnum.INCOMING_DELIVERIES,
            show: sbcPermissions.INCOMING_DELIVERIES,
            shortcutKey: UserShortcutEnum.INCOMING_DELIVERIES
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'STOCKTAKING.STOCKTAKING',
            icon: 'stocktaking',
            routerLink: '/warehouse/stocktaking',
            appName: AppNameEnum.STOCKTAKING,
            show: sbcPermissions.STOCKTRACKING,
            shortcutKey: UserShortcutEnum.STOCKTAKING
          },
          {
            title: 'APP.TRANSACTIONS',
            icon: 'stocktransfer',
            routerLink: '/warehouse/transactions',
            action: 'createTransaction',
            appName: AppNameEnum.TRANSACTIONS,
            show: sbcPermissions.WAREHOUSE_TRANSACTIONS,
            shortcutKey: UserShortcutEnum.TRANSACTIONS
          },
          {
            title: 'WAREHOUSE.WAREHOUSE_MANAGEMENT',
            icon: 'warehouse',
            routerLink: '/warehouse/warehouse-profile',
            shortcutKey: UserShortcutEnum.WAREHOUSE_DATA
            // show: sbcPermissions.WAREHOUSE_MANAGEMENT || isFreePlan,
          },
          {
            title: 'APP.SETTINGS',
            show: canManageWarehouse,
            icon: 'options-2',
            routerLink: sbcPermissions.WAREHOUSE_PREFERENCES
              ? '/warehouse/settings/delivery-services'
              : '/warehouse/settings/number-range',
          }
        ]
      }
    ],
  };

  const ECOMMERCE_ITEMS: SideNavigationModulesItem = {
    title: 'MODULE.E_COMMERCE',
    icon: 'shopping-cart',
    show: canViewEcommerce &&
      ((ecommerceEnabled && (isAccountingPlan || isTeamPlan))
        || sbcPermissions.ECOMMERCE_ORDERS
        || sbcPermissions.ECOMMERCE_CORPORATE_PARTNERS
        || sbcPermissions.ECOMMERCE_PRIVATE_PARTNERS
      ),
    color: GradientColor.RED,
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'ECOMMERCE.ORDERS',
            icon: 'sales-order',
            show: canViewEcommerce && sbcPermissions.ECOMMERCE_ORDERS,
            routerLink: `/e-commerce/orders`,
            appName: AppNameEnum.E_COMMERCE,
            shortcutKey: UserShortcutEnum.ECOMMERCE_ORDERS
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'APP.CUSTOMERS',
            show: canViewEcommerce && (corporatePartnersEnabled || sbcPermissions.ECOMMERCE_CORPORATE_PARTNERS),
            icon: 'factory',
            routerLink: `/e-commerce/customers/corporate`,
          },
          {
            title: 'APP.PRIVATE_PARTNERS',
            show: canViewEcommerce && (privatePartnersEnabled || sbcPermissions.ECOMMERCE_PRIVATE_PARTNERS),
            icon: 'person',
            routerLink: `/e-commerce/customers/private`,
          },
          // {
          //   title: 'APP.CORPORATE_CUSTOMERS',
          //   show: canViewEcommerce && (corporatePartnersEnabled || sbcPermissions.ECOMMERCE_CORPORATE_PARTNERS),
          //   icon: 'factory',
          //   routerLink: `/e-commerce/customers/corporate`,
          // },
          // {
          //   title: 'APP.PRIVATE_CUSTOMERS',
          //   show: canViewEcommerce && (privatePartnersEnabled || sbcPermissions.ECOMMERCE_PRIVATE_PARTNERS),
          //   icon: 'person',
          //   routerLink: `/e-commerce/customers/private`,
          // },
          {
            title: 'APP.SETTINGS',
            show: canViewEcommerce && canManageEcommerce,
            icon: 'options-2',
            routerLink: `/e-commerce/settings`,
          },
        ]
      },
    ]
  };

  const SERVICES_ITEMS: SideNavigationModulesItem = {
    title: 'APP.TIME_TRACKING',
    icon: 'checklist',
    show: canViewServices && (servicesEnabled || sbcPermissions.TIME_TRACKING),
    color: 'green',
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'APP.TIME_TRACKING',
            icon: 'timer',
            routerLink: '/services/time-tracking',
            disabled: false,
            action: 'onLogTimeClick',
            appName: AppNameEnum.TIME_TRACKING,
            shortcutKey: UserShortcutEnum.TIME_TRACKING
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'APP.SETTINGS',
            show: canManageServices && (servicesEnabled || sbcPermissions.TIME_TRACKING),
            icon: 'options-2',
            routerLink: '/services/settings'
          }
        ]
      }
    ]
  };

  const PARTNERS_ITEMS: SideNavigationModulesItem = {
    title: 'MODULE.PARTNERS',
    icon: 'heart',
    show: canViewPartners,
    color: 'brown',
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'LAUNCH_PAD.COMPANIES',
            icon: 'factory',
            show: corporatePartnersEnabled,
            routerLink: '/partners-new/corporate',
            action: 'createNewPartner',
            appName: AppNameEnum.CORPORATE_PARTNERS,
          },
          {
            title: 'APP.PRIVATE_PARTNERS',
            icon: 'person',
            show: privatePartnersEnabled,
            routerLink: '/partners-new/private',
            action: 'createNewPrivatePartner',
            appName: AppNameEnum.PRIVATE_PARTNERS,
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'APP.SETTINGS',
            icon: 'options-2',
            show: canManagePartners,
            routerLink: '/partners-new/settings'
          }
        ]
      }
    ]
  };

  const PRODUCT_ITEMS: SideNavigationModulesItem = {
    title: 'MODULE.PRODUCTS',
    icon: 'box',
    show: canViewProducts,
    color: GradientColor.PURPLE,
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'COMMON.GOODS',
            icon: 'box',
            show: goodsProductTypeEnabled,
            routerLink: `/products/${ProductTypes.GOODS}`,
            disabled: false,
            action: 'createNewProduct',
            appName: AppNameEnum.GOODS
          },
          {
            title: 'APP.SERVICES',
            icon: 'checklist',
            show: servicesProductTypeEnabled,
            routerLink: `/products/${ProductTypes.SERVICES}`,
            disabled: false,
            action: 'createNewService',
            appName: AppNameEnum.SERVICES
          },
          {
            title: 'APP.DIGITAL_PRODUCTS',
            icon: 'digital-product',
            show: digitalProductTypeEnabled,
            routerLink: `/products/${ProductTypes.DIGITAL}`,
            disabled: false,
            action: 'createNewDigitalProduct',
            appName: AppNameEnum.DIGITAL
          },
          {
            title: 'BUNDLES.BUNDLES',
            icon: 'bundle',
            show: bundleProductTypeEnabled,
            routerLink: `/products/${ProductTypes.BUNDLE}`,
            disabled: false,
            action: 'createNewBundleProduct',
            appName: AppNameEnum.BUNDLES
          },
        ]
      },
      {
        title: 'APP.MANAGEMENT',
        children: [
          {
            title: 'GL_ACCOUNTS.GENERAL_PRODUCTS',
            icon: 'dashboard',
            routerLink: `/products/general-products`,
            disabled: false,
            appName: AppNameEnum.GENERAL_PRODUCTS
          },
          {
            title: 'APP.CATEGORIES',
            icon: 'folder-open',
            routerLink: `/products/settings/categories/${productTypeRedirect}`,
            disabled: false,
            appName: AppNameEnum.CATEGORIES
          },
          {
            title: 'APP.SETTINGS',
            show: canManageProducts,
            icon: 'options-2',
            routerLink: '/products/products-settings'
          }
        ]
      }
    ]
  };

  const SYSTEM_ITEMS: SideNavigationModulesItem = {
    title: 'MODULE.SYSTEM',
    color: 'black',
    icon: 'r-logo',
    show: canViewAdministration || canViewAccounting || canViewWarehouse || canViewWholesale || canViewTeam,
    childrenGroups: [
      {
        title: '',
        children: [
          {
            title: 'MODULE.ADMINISTRATION',
            icon: 'settings',
            routerLink: '/administration/company-profile',
            show: canViewAdministration,
            appName: AppNameEnum.ADMINISTRATION
          },
          {
            title: 'DATA_IMPORT.DATA_IMPORT',
            icon: 'cloud-upload',
            routerLink: '/data-import',
            show: canViewAdministration,
            appName: AppNameEnum.DATA_IMPORT,
          },
          {
            title: 'PDF_SETTINGS.PDF_DESIGNER',
            icon: 'pdf-designer',
            routerLink: '/pdf-designer',
            show: canViewAdministration,
            appName: AppNameEnum.PDF_DESIGNER,
          },
          {
            title: 'APP.EMPLOYEES',
            icon: 'people',
            routerLink: '/team',
            action: 'createNewEmployee',
            show: canViewTeam,
            appName: AppNameEnum.EMPLOYEES,
            shortcutKey: UserShortcutEnum.EMPLOYEES
          }
        ]
      },
    ]
  };

  return [
    ACCOUNTING_ITEMS,
    TRADE_ITEMS,
    WAREHOUSE_ITEMS,
    ECOMMERCE_ITEMS,
    SERVICES_ITEMS,
    PARTNERS_ITEMS,
    PRODUCT_ITEMS,
    SYSTEM_ITEMS
  ];
}
